import { differenceInDays } from 'date-fns';
import { Model } from 'json-api-models';

import { Models, Schemas } from '../models.ts';
import { useGlobalStore } from '../stores/global.ts';
import { nullableDate } from '../utils/format.ts';
import { Contribution } from './contribution.ts';

export type ItemContentType = NonNullable<Schemas['items']['attributes']>['contentType'];

/** Get the display title of item content, which is a name or title by type. */
export function contentTitle(content: object | null | undefined) {
    if (content && 'name' in content && typeof content.name === 'string') return content.name;
    if (content && 'title' in content && typeof content.title === 'string') return content.title;
}

export class Item extends Model<Schemas['items'], Models> {
    get createdAtDate() {
        return nullableDate(this.createdAt);
    }

    get updatedAtDate() {
        return nullableDate(this.updatedAt);
    }

    get acceptedAtDate() {
        return nullableDate(this.acceptedAt);
    }

    get staleAtDate() {
        return nullableDate(this.staleAt);
    }

    get statisticsResetAtDate() {
        return nullableDate(this.statisticsResetAt);
    }

    /** Find a revision by number, or the latest revision when none is given. */
    revision(number?: number) {
        if (!number) return this.lastRevision || this.activeRevision;

        return this.lastRevision?.number === number
            ? this.lastRevision
            : this.revisions?.find((candidate) => candidate.number === number);
    }

    /**
     * Whether the learner app should show a subscribe prompt instead of the item.
     *
     * Staff who can revise the item still see it, so they can preview drafts
     * that nobody can access yet.
     */
    get isLocked(): boolean {
        return !this.meta?.canAccess && !this.meta?.canRevise;
    }

    get editRoute() {
        return {
            name: this.contentType === 'exam' ? 'items.redirect' : `${this.contentType}s.edit`,
            params: { id: this.id },
        };
    }

    get daysSinceUpdated(): number {
        return differenceInDays(new Date(), this.updatedAtDate);
    }

    get isInactive(): boolean {
        return this.currentContributionCount > 0 && this.daysSinceUpdated >= 14;
    }

    get currentContributionCount(): number {
        return this.currentContributions.length;
    }

    get currentContributions(): Contribution[] {
        const identifiers =
            this.relationships.currentContributions?.data ??
            this.relationships.contributions?.data ??
            [];

        if (!Array.isArray(identifiers)) return [];

        return identifiers
            .map((identifier) => this.store?.find(identifier) as Contribution | null | undefined)
            .filter(
                (contribution): contribution is Contribution =>
                    contribution instanceof Contribution && !contribution.finalizedAt,
            );
    }

    get pastContributions(): Contribution[] {
        return this.contributions?.filter((c) => c.finalizedAt) || [];
    }

    get myContribution(): Contribution | undefined {
        return this.myContributions[0];
    }

    get myContributions(): Contribution[] {
        const actor = useGlobalStore().actor;
        return this.currentContributions.filter((c) => c.user === actor || this.meta?.canManage);
    }
}
